import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/dialog-constants";
import { getBulkActionButtonConfig } from "@components/common/faro-table/bulk-actions/bulk-actions-config";
import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { BulkSuccessDialog } from "@components/common/faro-table/bulk-dialog/bulk-success-dialog";
import { FaroTextButton } from "@components/common/faro-text-button";
import { useAddToTeamBulkDialogContent } from "@components/table/members/bulk-actions/bulk-add-to-team-helper";
import { ADD_TO_TEAM_LIMIT_MESSAGE, ADD_TO_TEAM_TITLE } from "@components/teams/add-to-team/add-to-team-constants";
import { AddToTeamNotes } from "@components/teams/add-to-team/add-to-team-notes";
import { TeamSelect } from "@components/teams/add-to-team/team-select";
import { useAddMemberToTeam } from "@components/teams/add-to-team/use-add-member-to-team";
import { MemberTeamsLoadError } from "@components/teams/add-to-team/member-teams-load-error";
import { useMembersTeamIds } from "@components/teams/add-to-team/use-members-team-ids";
import { useMarkMembersInTeam } from "@components/table/members/bulk-actions/use-mark-members-in-team";
import { MAX_INVITED_MEMBERS } from "@components/teams/teams-constants";
import { TeamIcon, Text } from "@flat-ui";
import { useBulkActionsMode } from "@hooks/use-bulk-actions-mode";
import { useTeamUtils } from "@hooks/use-team-utils";
import { TeamTabs } from "@router/route-params";
import { Stack } from "@mui-wrapper";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { getTeamByIdSelector } from "@store/teams/teams-selector";
import { bulkActionNameSelector, isBulkActionProcessingSelector, selectedEntitiesSelector, selectedRowIdsSelector, shouldShowFailedDialogSelector, shouldShowSuccessDialogSelector } from "@store/table/table-selector";
import { setBulkActionName, setInitialFetchingItems } from "@store/table/table-slice";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { useEffect, useState } from "react";

/**
 * Button that adds the selected workspace members to a team, after the team is picked and the
 * choice is confirmed in a dialog.
 *
 * One action takes at most {@link MAX_INVITED_MEMBERS} members, so the button is disabled for a
 * bigger selection. Members who are in the picked team already stay listed in the dialog, but are
 * left out of the requests.
 */
export function BulkAddToTeam() {
  const dispatch = useAppDispatch();
  const addMemberToTeam = useAddMemberToTeam();
  const {
    openTeamDetails
  } = useTeamUtils();
  const mode = useBulkActionsMode();
  const [shouldShowDialog, setShouldShowDialog] = useState(false);
  const [team, setTeam] = useState();
  // The members the dialog was opened for, so that removing one from the list does not load the
  // teams of the others again
  const [dialogMembers, setDialogMembers] = useState([]);
  const storedTeam = useAppSelector(getTeamByIdSelector(team?.id));
  const bulkActionName = useAppSelector(bulkActionNameSelector);
  const isBulkActionProcessing = useAppSelector(isBulkActionProcessingSelector);
  const shouldShowSuccessDialog = useAppSelector(shouldShowSuccessDialogSelector);
  const shouldShowFailedDialog = useAppSelector(shouldShowFailedDialogSelector);
  const selectedEntities = useAppSelector(selectedEntitiesSelector("members"));
  const selectedRowIds = useAppSelector(selectedRowIdsSelector);
  const memberTeams = useMembersTeamIds(dialogMembers, shouldShowDialog);
  // Until the teams of the members are known, no team is known to be safe to pick
  const canPickTeam = !memberTeams.isLoading && !memberTeams.hasFailed;
  const {
    bulkDialogContent,
    successDialogContent
  } = useAddToTeamBulkDialogContent();
  const isOverLimit = selectedEntities.length > MAX_INVITED_MEMBERS;
  // Once the members were sent, the remaining ones can only be retried for the same team
  const canChangeTeam = !isBulkActionProcessing && !shouldShowFailedDialog && !shouldShowSuccessDialog;
  const membersToAdd = useMarkMembersInTeam({
    members: selectedEntities,
    teamIdsByMember: memberTeams.teamIdsByMember,
    teamId: team?.id,
    isEnabled: shouldShowDialog && canChangeTeam
  });
  const numberOfMembersInTeam = selectedEntities.length - membersToAdd.length;

  // Closes the bulk dialog when the bulk action is successful to show the success dialog
  useEffect(() => {
    if (shouldShowSuccessDialog) {
      setShouldShowDialog(false);
    }
  }, [shouldShowSuccessDialog]);
  const config = getBulkActionButtonConfig(mode, isOverLimit ? ADD_TO_TEAM_LIMIT_MESSAGE : ADD_TO_TEAM_TITLE);
  return <>
      <FaroTextButton onClick={openDialog} isDisabled={isOverLimit} tooltipText={config.tooltipText} rippleColor={config.rippleColor} startIcon={<TeamIcon sx={{
      width: "18px",
      height: "18px"
    }} />} sx={config.sx}>
        {config.shouldShowText && <Stack>
            <Text variant="buttonM">{ADD_TO_TEAM_TITLE}</Text>
            {/* The collapsed menu shows no tooltips, so the reason is spelled out below the label */}
            {isOverLimit && !config.tooltipText && <Text variant="bodyXS">{ADD_TO_TEAM_LIMIT_MESSAGE}</Text>}
          </Stack>}
      </FaroTextButton>

      {/* Checking for bulkActionName to make sure the dialog of another bulk action is not opened by mistake */}
      {bulkActionName === "addToTeam" && shouldShowSuccessDialog && <BulkSuccessDialog dialogContent={successDialogContent} secondaryAction={team ? {
      text: `Go to ${TEAM_DISPLAY_NAME}`,
      onClick: () => openTeamDetails({
        teamId: team.id,
        // The picked team was copied before the members joined it
        numberOfMembers: storedTeam?.memberCount ?? team.memberCount,
        tab: TeamTabs.members
      })
    } : undefined} />}

      {bulkActionName === "addToTeam" && shouldShowDialog && <FaroBulkDialog dialogContent={bulkDialogContent} onClose={closeDialog} trackingEvent={BulkActionEvents.bulkAddMembersToTeam} shouldDisableConfirmButton={!team || !canPickTeam} bulkActionCallback={addSelectedMember} selectedEntities={selectedEntities} uniqueIdKey="identity" tableDataType="members">
          <Stack sx={{
        gap: 1,
        mb: SPACE_ELEMENTS_OF_MODAL
      }}>
            {memberTeams.hasFailed && <MemberTeamsLoadError numberOfMembers={dialogMembers.length} onRetry={memberTeams.retry} />}

            <TeamSelect selectedTeamId={team?.id} onTeamChange={setTeam} isDisabled={!canChangeTeam || !canPickTeam} />

            {numberOfMembersInTeam > 0 && canChangeTeam && <Text variant="bodyS" sx={{
          color: "text.secondary"
        }}>
                <var>{numberOfMembersInTeam}</var> of{" "}
                <var>{selectedEntities.length}</var> selected members{" "}
                {numberOfMembersInTeam === 1 ? "is" : "are"} in this{" "}
                {TEAM_DISPLAY_NAME} already and won&apos;t be added again.
              </Text>}

            {team && membersToAdd.length > 0 && <AddToTeamNotes team={team} members={membersToAdd} />}
          </Stack>
        </FaroBulkDialog>}
    </>;

  /** Opens the dialog with no team picked yet */
  function openDialog() {
    resetDialog();
    setDialogMembers(selectedEntities);
    dispatch(setBulkActionName("addToTeam"));
    setShouldShowDialog(true);
  }

  /** Closes the dialog without adding anybody. Cancelling the bulk dialog resets the rows already */
  function closeDialog() {
    setTeam(undefined);
    setShouldShowDialog(false);
  }

  /**
   * Forgets the picked team and clears the marks it left on the selected members, so that they do
   * not carry over to the next bulk action.
   */
  function resetDialog() {
    setTeam(undefined);
    dispatch(setInitialFetchingItems(selectedRowIds));
  }

  /** Adds one of the selected members to the picked team */
  async function addSelectedMember(member) {
    // The confirm button stays disabled until a team is picked
    if (!team) {
      throw new Error(`No ${TEAM_DISPLAY_NAME} was picked`);
    }
    await addMemberToTeam({
      teamId: team.id,
      member
    });
  }
}